import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import {
  BellRing, Plus, Trash2, Clock, Eye, Loader2, CalendarClock,
  CheckCircle2, AlertTriangle, Send, Users, Camera, Check,
} from 'lucide-react';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { ReminderPreviewDialog } from './ReminderPreviewDialog';

const TIMING = {
  reminder: [
    { value: '1w', label: '1 week before' },
    { value: '24h', label: '24 hours before' },
    { value: '1h', label: '1 hour before' },
    { value: 'custom_offset', label: 'Custom time before…' },
    { value: 'absolute', label: 'Specific date & time…' },
  ],
  followup: [
    { value: '24h', label: '24 hours after' },
    { value: '48h', label: '48 hours after' },
    { value: 'custom_offset', label: 'Custom time after…' },
    { value: 'absolute', label: 'Specific date & time…' },
  ],
};

const AUDIENCE = {
  reminder: [
    { value: 'all', label: 'Everyone invited' },
    { value: 'non_responders', label: "Non-responders (haven't replied)" },
    { value: 'going', label: 'Guests who RSVP\u2019d Going' },
    { value: 'maybe', label: 'Guests who RSVP\u2019d Maybe' },
  ],
  followup: [
    { value: 'all', label: 'Everyone invited' },
    { value: 'going', label: 'Guests who RSVP\u2019d Going' },
    { value: 'attended', label: 'Guests who attended (checked in)' },
  ],
};

const PRESET_VALUES = ['1w', '24h', '1h', '48h'];
const MAX_PHOTOS = 3;

const STATUS_META = {
  scheduled: { label: 'Scheduled', cls: 'text-primary' },
  sent: { label: 'Sent', cls: 'text-emerald-600 dark:text-emerald-400' },
  sending: { label: 'Sending…', cls: 'text-primary' },
  skipped: { label: 'Missed (too soon)', cls: 'text-amber-600 dark:text-amber-400' },
  failed: { label: 'Failed', cls: 'text-destructive' },
  canceled: { label: 'Canceled', cls: 'text-muted-foreground' },
};

const fmtInTz = (iso, tz) => {
  if (!iso) return 'TBA';
  try {
    return new Intl.DateTimeFormat('en-US', {
      weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit', timeZone: tz || undefined,
    }).format(new Date(iso));
  } catch (e) {
    return new Date(iso).toLocaleString();
  }
};

const ModeButton = ({ value, icon: Icon, label, active, onSelect }) => (
  <button
    type="button"
    onClick={() => onSelect(value)}
    data-testid={`schedule-mode-${value}`}
    className={`flex-1 flex items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
      active ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
    }`}
  >
    <Icon className="h-4 w-4" /> {label}
  </button>
);

export const SchedulePanel = ({ event }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const [mode, setMode] = useState('reminder'); // reminder | followup
  const [timing, setTiming] = useState('24h');
  const [offsetValue, setOffsetValue] = useState(3);
  const [offsetUnit, setOffsetUnit] = useState('days');
  const [absoluteAt, setAbsoluteAt] = useState('');
  const [audience, setAudience] = useState('all');
  const [note, setNote] = useState('');

  const [photos, setPhotos] = useState([]);
  const [selectedPhotos, setSelectedPhotos] = useState([]);

  const load = useCallback(() => {
    api.get(`/events/${event.id}/schedule`)
      .then((r) => setData(r.data))
      .catch(() => toast.error('Could not load schedule'))
      .finally(() => setLoading(false));
  }, [event.id]);

  useEffect(() => {
    const t = setTimeout(() => {
      load();
      api.get('/landing/photos', { params: { scope: 'event', event_id: event.id } })
        .then((r) => setPhotos(r.data || []))
        .catch(() => {});
    }, 0);
    return () => clearTimeout(t);
  }, [load, event.id]);

  const switchMode = (m) => {
    setMode(m);
    setTiming('24h');
    setAudience('all');
    if (m === 'reminder') setSelectedPhotos([]);
  };

  const togglePhoto = (id) => {
    setSelectedPhotos((prev) => {
      if (prev.includes(id)) return prev.filter((p) => p !== id);
      if (prev.length >= MAX_PHOTOS) { toast.warning(`You can attach up to ${MAX_PHOTOS} photos`); return prev; }
      return [...prev, id];
    });
  };

  const tz = data?.timezone || event.timezone;
  const isRelative = timing !== 'absolute';
  const needsDatetime = isRelative && !data?.has_datetime;

  const buildBody = () => {
    let body = { audience, note };
    if (timing === 'absolute') {
      if (!absoluteAt) { toast.error('Pick a date and time'); return null; }
      body = { ...body, offset_kind: 'absolute', absolute_at: absoluteAt };
    } else if (timing === 'custom_offset') {
      if (!offsetValue || offsetValue <= 0) { toast.error('Enter how long before/after the event'); return null; }
      body = { ...body, offset_kind: 'custom_offset', offset_value: Number(offsetValue), offset_unit: offsetUnit };
    } else {
      body = { ...body, offset_kind: 'preset', preset: timing };
    }
    if (mode === 'followup') body.photo_ids = selectedPhotos;
    return body;
  };

  const add = async () => {
    const body = buildBody();
    if (!body) return;
    setAdding(true);
    try {
      const endpoint = mode === 'followup' ? 'followups' : 'reminders';
      const r = await api.post(`/events/${event.id}/${endpoint}`, body);
      if (r.data?.status === 'skipped') {
        toast.warning('Added, but this time has already passed — it won\u2019t send.');
      } else {
        toast.success(mode === 'followup' ? 'Follow-up scheduled' : 'Reminder scheduled');
      }
      setNote('');
      setSelectedPhotos([]);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not schedule');
    } finally {
      setAdding(false);
    }
  };

  const removeJob = async (jobId) => {
    try {
      await api.delete(`/events/${event.id}/schedule/${jobId}`);
      toast.success('Removed');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not remove');
    }
  };

  const jobs = data?.jobs || [];
  const unitSuffix = mode === 'followup' ? 'after' : 'before';

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="schedule-panel">
      {/* Builder */}
      <div className="lg:col-span-1 space-y-4">
        <div className="rounded-2xl border border-border bg-card p-5">
          {/* Mode toggle */}
          <div className="flex gap-1 p-1 rounded-xl bg-secondary mb-4" data-testid="schedule-mode-toggle">
            <ModeButton value="reminder" icon={BellRing} label="Reminder" active={mode === 'reminder'} onSelect={switchMode} />
            <ModeButton value="followup" icon={Camera} label="Follow-up" active={mode === 'followup'} onSelect={switchMode} />
          </div>

          <p className="text-sm text-muted-foreground mb-4">
            {mode === 'followup'
              ? `Sent automatically after the event${tz ? ` (${tz})` : ''} — say thanks and share photos.`
              : `Sent automatically before the event${tz ? ` (${tz})` : ''}.`}
          </p>

          <div className="space-y-3.5">
            <div className="space-y-1.5">
              <label className="text-sm font-medium">When to send</label>
              <Select value={timing} onValueChange={setTiming}>
                <SelectTrigger data-testid="reminder-timing-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TIMING[mode].map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {timing === 'custom_offset' && (
              <div className="flex gap-2" data-testid="reminder-custom-offset">
                <Input
                  type="number" min={1} value={offsetValue}
                  onChange={(e) => setOffsetValue(e.target.value)}
                  className="w-24" data-testid="reminder-offset-value"
                />
                <Select value={offsetUnit} onValueChange={setOffsetUnit}>
                  <SelectTrigger className="flex-1" data-testid="reminder-offset-unit"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="minutes">{`minutes ${unitSuffix}`}</SelectItem>
                    <SelectItem value="hours">{`hours ${unitSuffix}`}</SelectItem>
                    <SelectItem value="days">{`days ${unitSuffix}`}</SelectItem>
                    <SelectItem value="weeks">{`weeks ${unitSuffix}`}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {timing === 'absolute' && (
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Date & time</label>
                <Input
                  type="datetime-local" value={absoluteAt}
                  onChange={(e) => setAbsoluteAt(e.target.value)}
                  data-testid="reminder-absolute-input"
                />
              </div>
            )}

            <div className="space-y-1.5">
              <label className="text-sm font-medium">Send to</label>
              <Select value={audience} onValueChange={setAudience}>
                <SelectTrigger data-testid="reminder-audience-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {AUDIENCE[mode].map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-medium">Note (optional)</label>
              <Textarea
                rows={3} value={note} onChange={(e) => setNote(e.target.value)}
                placeholder={mode === 'followup' ? 'A thank-you message to your guests…' : 'A short message to include…'}
                data-testid="reminder-note-input"
              />
            </div>

            {/* Photo picker (follow-up only) */}
            {mode === 'followup' && (
              <div className="space-y-2" data-testid="followup-photo-picker">
                <label className="text-sm font-medium flex items-center justify-between">
                  <span>Attach photos</span>
                  <span className="text-xs text-muted-foreground">{selectedPhotos.length}/{MAX_PHOTOS}</span>
                </label>
                {photos.length === 0 ? (
                  <p className="text-xs text-muted-foreground rounded-lg bg-secondary/50 px-3 py-2" data-testid="followup-no-photos">
                    No photos yet. Upload event photos in the “Photos” tab to attach them here.
                  </p>
                ) : (
                  <div className="grid grid-cols-3 gap-2">
                    {photos.map((p) => {
                      const sel = selectedPhotos.includes(p.id);
                      return (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => togglePhoto(p.id)}
                          data-testid="followup-photo-option"
                          className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-colors ${
                            sel ? 'border-primary' : 'border-transparent hover:border-border'
                          }`}
                        >
                          <img src={p.thumb_url || p.url} alt={p.caption || 'Event photo'} className="w-full h-full object-cover" loading="lazy" />
                          {sel && (
                            <span className="absolute inset-0 bg-primary/30 flex items-center justify-center">
                              <span className="h-6 w-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center">
                                <Check className="h-3.5 w-3.5" />
                              </span>
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {needsDatetime && (
              <div className="text-xs rounded-lg bg-amber-500/10 border border-amber-500/25 text-amber-700 dark:text-amber-300 px-3 py-2 flex items-start gap-2" data-testid="reminder-needs-datetime">
                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                Set the event date & time (Edit tab) to use relative timing, or pick a specific date & time.
              </div>
            )}

            <div className="flex gap-2 pt-1">
              <Button variant="outline" className="flex-1 gap-2" onClick={() => setPreviewOpen(true)} data-testid="reminder-preview-button">
                <Eye className="h-4 w-4" /> Preview
              </Button>
              <Button className="flex-1 gap-2" onClick={add} disabled={adding || needsDatetime} data-testid="reminder-add-button">
                {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Scheduled list */}
      <div className="lg:col-span-2">
        <div className="rounded-2xl border border-border bg-card overflow-hidden">
          <div className="p-4 border-b border-border flex items-center gap-2">
            <CalendarClock className="h-[18px] w-[18px] text-primary" />
            <h3 className="font-display text-lg font-semibold">Scheduled emails</h3>
            <Badge variant="secondary" className="ml-auto" data-testid="schedule-count">{jobs.length}</Badge>
          </div>

          {loading ? (
            <div className="p-4 space-y-2">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-16 rounded-xl" />)}</div>
          ) : jobs.length === 0 ? (
            <div className="p-10 text-center" data-testid="schedule-empty">
              <BellRing className="h-8 w-8 text-muted-foreground/50 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">No scheduled emails yet.<br />Add a reminder or a post-event follow-up to get started.</p>
            </div>
          ) : (
            <div className="divide-y divide-border" data-testid="schedule-list">
              {jobs.map((j) => {
                const meta = STATUS_META[j.status] || STATUS_META.scheduled;
                const isInvite = j.kind === 'invite';
                const isFollowup = j.kind === 'followup';
                const canDelete = !['sent', 'sending'].includes(j.status);
                const RowIcon = isInvite ? Send : isFollowup ? Camera : BellRing;
                const photoCount = (j.photo_ids || []).length;
                return (
                  <div key={j.id} className="flex items-center gap-3 px-4 py-3.5" data-testid="schedule-row">
                    <div className={`h-9 w-9 rounded-xl flex items-center justify-center shrink-0 ${isInvite ? 'bg-primary/10' : isFollowup ? 'bg-accent/10' : 'bg-secondary'}`}>
                      <RowIcon className={`h-4 w-4 ${isInvite || isFollowup ? 'text-primary' : 'text-foreground'}`} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium truncate">
                          {isInvite ? 'Invitation' : isFollowup ? `Follow-up · ${j.label || ''}` : (j.label || 'Reminder')}
                        </span>
                        <span className={`text-xs font-medium ${meta.cls}`} data-testid="schedule-status">{meta.label}</span>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5 flex-wrap">
                        <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {fmtInTz(j.run_at, tz)}</span>
                        <span className="flex items-center gap-1">
                          <Users className="h-3 w-3" />
                          {isInvite ? `${(j.contact_ids || []).length} recipient${(j.contact_ids || []).length !== 1 ? 's' : ''}` : j.audience_label}
                        </span>
                        {isFollowup && photoCount > 0 && (
                          <span className="flex items-center gap-1"><Camera className="h-3 w-3" /> {photoCount} photo{photoCount !== 1 ? 's' : ''}</span>
                        )}
                        {j.status === 'sent' && j.result && (
                          <span className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
                            <CheckCircle2 className="h-3 w-3" /> {j.result.sent ?? 0} sent
                          </span>
                        )}
                      </div>
                    </div>
                    {canDelete && (
                      <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-destructive shrink-0" onClick={() => removeJob(j.id)} data-testid="schedule-delete-button">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <ReminderPreviewDialog
        event={event} kind={mode} note={note} photoIds={selectedPhotos}
        open={previewOpen} onOpenChange={setPreviewOpen}
      />
    </div>
  );
};
